import { KontoausgleicheArt } from '@/domain/mitarbeiter/features/feiertagskonto/entities';
import { useDeleteKontoausgleich } from '@/domain/mitarbeiter/features/feiertagskonto/hooks/useDeleteKontoausgleich/useDeleteKontoausgleich';
import { useIsMobile } from '@/hooks';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  toast,
} from '@e2n/ui/v2';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

type DeleteKontoausgleichDialogProps = {
  kontoausgleichId: string;
  datum: string;
  art: KontoausgleicheArt;
};

export function DeleteKonotausgleichDialog({
  kontoausgleichId,
  datum,
  art,
}: DeleteKontoausgleichDialogProps) {
  const { id } = useParams();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { deleteKontoausgleich } = useDeleteKontoausgleich();
  const isMobile = useIsMobile();
  async function handleDelete() {
    try {
      const result = await deleteKontoausgleich({
        input: {
          id: kontoausgleichId,
          mitarbeiterId: id,
        },
      });
      if (result.error) {
        toast({
          variant: 'error',
          description: t(
            'domain.mitarbeiter.feiertagskonto.kontoausgleich.deleteKontoausgleich.failure',
          ),
        });
      } else {
        toast({
          variant: 'success',
          description: t(
            'domain.mitarbeiter.feiertagskonto.kontoausgleich.deleteKontoausgleich.success',
          ),
        });
      }
    } catch (_error) {
      toast({
        variant: 'error',
        description: t(
          'domain.mitarbeiter.feiertagskonto.kontoausgleich.deleteKontoausgleich.error',
        ),
      });
    } finally {
      setOpen(false);
    }
  }

  return !isMobile ? (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="secondary-red"
          iconOnly
          size="small"
          aria-label={t('actions.löschen')}>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(
              'domain.mitarbeiter.feiertagskonto.kontoausgleich.deleteKontoausgleich.title',
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'domain.mitarbeiter.feiertagskonto.kontoausgleich.deleteKontoausgleich.description',
              {
                datum: dayjs(datum).format('dd., DD.MM.YYYY'),
                art: art,
              },
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter variant="default">
          <DialogClose asChild>
            <Button variant="secondary-blue">{t('actions.abbrechen')}</Button>
          </DialogClose>
          <Button variant="primary-pill" onClick={handleDelete}>
            <FontAwesomeIcon icon={faTrash} className="size-4" />
            {t('actions.löschen')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="secondary-red"
          iconOnly
          size="small"
          aria-label={t('actions.löschen')}>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>
            {t(
              'domain.mitarbeiter.feiertagskonto.kontoausgleich.deleteKontoausgleich.title',
            )}
          </DrawerTitle>
          <DrawerDescription>
            {t(
              'domain.mitarbeiter.feiertagskonto.kontoausgleich.deleteKontoausgleich.description',
              {
                datum: dayjs(datum).format('dd., DD.MM.YYYY'),
                art: art,
              },
            )}
          </DrawerDescription>
        </DrawerHeader>
        <DrawerBody>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="secondary-blue">{t('actions.abbrechen')}</Button>
            </DrawerClose>
            <Button variant="primary-pill" onClick={handleDelete}>
              <FontAwesomeIcon icon={faTrash} className="size-4" />
              {t('actions.löschen')}
            </Button>
          </DrawerFooter>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
